import React, { JSX } from 'react'
import { AnalyticsMetafield, CustomerStats } from 'types/api'
import { cleanVariables, graphqlRequest, moneyFormatter } from 'lib/helpers'
import { RefreshingBlock } from './Loading'
import getCustomerStatistics from '../graphql/getCustomerStatistics.graphql'
import { useQuery } from '@tanstack/react-query'
import { EmptyDataCard } from './EmptyDataCard'
import { IconArrayChart } from './IconArray'
import { RadialWithDelta } from './RadialWithDelta'

interface CustomerStatisticsSectionProps {
  filters: {
    productOption?: AnalyticsMetafield[]
    bundleOption?: AnalyticsMetafield[]
    range: {
      startDate: Date
      endDate: Date
    }
    project?: AnalyticsMetafield
  }
  token?: string
  client: AnalyticsMetafield | null
}

export const CustomerStatisticsSection = ({ token, client, filters }: CustomerStatisticsSectionProps): JSX.Element => {
  const {
    data,
    isFetching
  } = useQuery({
    queryKey: ['dashboard', 'customerStatistics', filters, client?.id],

    queryFn: async () => {
      if (token == null) return

      const productFilters = filters?.productOption
        ?.flatMap(option =>
          (option.components != null) && option.components.length > 0
            ? option.components
            : [option.id]
        ) ?? []

      const cleanVar = cleanVariables({
        clientId: client?.id,
        projectId: filters.project?.id,
        productIds: productFilters,
        startDate: filters.range.startDate,
        endDate: filters.range.endDate
      })

      const { customerStats } = await graphqlRequest<{ customerStats: CustomerStats }>(
        getCustomerStatistics, cleanVar, token
      )

      return customerStats
    },

    enabled: token != null && client != null,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  })

  if (isFetching) {
    return (
      <>
        <div className='relative shadow-sm dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 rounded-none md:rounded-2xl overflow-hidden p-4 min-h-[280px] flex flex-col gap-4'>
          {RefreshingBlock}
          <EmptyDataCard message='No data available for specified filters' />
        </div>
        <div className='relative shadow-sm rounded-none md:rounded-2xl overflow-hidden p-4 min-h-[280px] flex flex-col gap-4'>
          {RefreshingBlock}
          <EmptyDataCard message='No data available for specified filters' />
        </div>
      </>
    )
  }

  const isAllDataZero = data != null &&
    data.averageOrderValue === 0 &&
    data.maxOrderValue === 0 &&
    data.minOrderValue === 0 &&
    data.percentageOfMerchTicketBuyers === 0 &&
    data.percentageOfInEventPurchases === 0 &&
    data.percentageOfRepeatCustomers === 0

  if (data == null || isAllDataZero) {
    return (
      <>
        <div data-testid='empty-customer-data' className='relative shadow-sm dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 rounded-none md:rounded-2xl overflow-hidden p-4 min-h-[280px] flex flex-col gap-4'>
          <EmptyDataCard message='No data available for specified filters' />
        </div>
        <div data-testid='empty-icon-data' className='relative shadow-sm dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 rounded-none md:rounded-2xl overflow-hidden p-4 min-h-[280px] flex flex-col'>
          <EmptyDataCard message='No data available for specified filters' />
        </div>
      </>
    )
  }

  return (
    <>
      <div className='relative shadow-sm dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 rounded-none md:rounded-2xl p-4 min-h-[300px] flex flex-col'>
        <div className='pt-2 px-4 mb-2'><h2 className='text-sm font-medium'>Order Statistics</h2></div>

        <div className='flex-1 flex flex-col items-center justify-center'>

          <div className='w-4/5 aspect-square max-w-[270px]'>
            <RadialWithDelta
              percent={(data?.averageOrderValue / data?.maxOrderValue) * 100}
              averageValue={data?.averageOrderValue}
              strokeWidth={18}
            />
          </div>
          <div className='text-sm text-center pb-4'>
            <div className='text-lm-text-secondary dark:text-dm-text-secondary font-medium mt-1'>
              Highest order value: <span className='font-semibold'>{`${moneyFormatter(data?.maxOrderValue)}`}</span>
            </div>
            <div className='text-lm-text-secondary dark:text-dm-text-secondary font-medium mt-1'>
              Lowest order value: <span className='font-semibold'>{`${moneyFormatter(data?.minOrderValue)}`}</span>
            </div>
          </div>

        </div>
      </div>
      <div className='relative shadow-sm dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 rounded-none md:rounded-2xl overflow-hidden p-4 min-h-[280px] flex flex-col'>
        <IconArrayChart percentage={data?.percentageOfMerchTicketBuyers} title='Upsell Conversion' caption='out of 100 fans purchased an add on with an event ticket' />
      </div>
    </>
  )
}
